import React, { memo, useState } from 'react'
import get from 'lodash/get'
import ControlledSingleSelect from 'transfix-form/components/ControlledSingleSelect/ControlledSingleSelect'
import EditIcon from 'transfix-icons/build/EditIcon'
import { Card } from 'transfix-ui/components/Card/Card'
import IconButton from 'transfix-ui/components/IconButton'
import InlineMessage, { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { capitalize, Box } from 'transfix-ui/core'
import Grid from 'transfix-ui/core/Grid/Grid'
import Typography from 'transfix-ui/core/Typography/Typography'
import { IShipperRateUpdatesAllowed, IStatusTimezoneScheme } from 'types/graphqlTypes'
import { EditingForm } from 'apps/marketplace/pages/shipment-details/components/EditingForm'
import { DISPLAY } from 'constants/display'
import { Pill } from './Pill'
import { useShipperEdiUpdateSettingsForm } from './forms/ShipperEdiUpdateSettingsForm/useshipperEdiUpdateSettingsForm'
import { useUpdateShipperApplicationEdiConfigMutation } from './graphql/UpdateShipperApplicationEdiConfig'

interface IShipperEdiUpdateSettingsProps {
  statusTimezoneScheme?: string | null
  sendsAppointmentMessagesOnlyWhenConfirmed?: boolean | null
  sendsAppointmentTimesOnShipmentCreation?: boolean | null
  shipperRateUpdatesAllowed?: IShipperRateUpdatesAllowed | null
  configId?: string | null
}

const timezoneMap = {
  [IStatusTimezoneScheme.eastern]: 'Eastern',
  [IStatusTimezoneScheme.local]: 'Local',
  [IStatusTimezoneScheme.local_with_lt]: 'Local with LT',
  [IStatusTimezoneScheme.utc]: 'UTC',
}

const shipperRateUpdatesAllowedMap = {
  [IShipperRateUpdatesAllowed.after_delivery]: '7 Days After Delivery',
  [IShipperRateUpdatesAllowed.never]: 'Never',
  [IShipperRateUpdatesAllowed.until_delivery]: 'Until Delivery',
}

const TIMEZONE_SCHEME_OPTIONS = [
  { value: IStatusTimezoneScheme.eastern, label: 'Eastern' },
  { value: IStatusTimezoneScheme.local, label: 'Local' },
  { value: IStatusTimezoneScheme.local_with_lt, label: 'Local with LT' },
  { value: IStatusTimezoneScheme.utc, label: 'UTC' },
]

const BOOLEAN_OPTIONS = [
  { value: 'True', label: 'True' },
  { value: 'False', label: 'False' },
]

const SHIPPER_RATE_UPDATES_ALLOWED_OPTIONS = [
  { value: IShipperRateUpdatesAllowed.after_delivery, label: '7 Days After Delivery' },
  { value: IShipperRateUpdatesAllowed.until_delivery, label: 'Until Delivery' },
  { value: IShipperRateUpdatesAllowed.never, label: 'Never' },
]

export const ShipperEdiUpdateSettingsCard = memo<IShipperEdiUpdateSettingsProps>(
  ({
    configId,
    sendsAppointmentMessagesOnlyWhenConfirmed,
    sendsAppointmentTimesOnShipmentCreation,
    shipperRateUpdatesAllowed,
    statusTimezoneScheme,
  }) => {
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const toggleEditing = () => setIsEditing(edit => !edit)

    const rows = [
      {
        key: 'statusTimezoneScheme',
        label: 'Status Timezone Scheme',
        selectLabel: timezoneMap[statusTimezoneScheme as IStatusTimezoneScheme],
        value: statusTimezoneScheme,
        options: TIMEZONE_SCHEME_OPTIONS,
      },
      {
        key: 'sendsAppointmentMessagesOnlyWhenConfirmed',
        label: 'Sends Appointment Messages Only When Confirmed',
        selectLabel: sendsAppointmentMessagesOnlyWhenConfirmed ? 'True' : 'False',
        value: sendsAppointmentMessagesOnlyWhenConfirmed,
        options: BOOLEAN_OPTIONS,
      },
      {
        key: 'sendsAppointmentTimesOnShipmentCreation',
        label: 'Sends Appointment Times On Shipment Creation',
        selectLabel: sendsAppointmentTimesOnShipmentCreation ? 'True' : 'False',
        value: sendsAppointmentTimesOnShipmentCreation,
        options: BOOLEAN_OPTIONS,
      },
      {
        key: 'shipperRateUpdatesAllowed',
        label: 'Shipper Rate Updates Allowed',
        selectLabel:
          shipperRateUpdatesAllowedMap[shipperRateUpdatesAllowed as IShipperRateUpdatesAllowed],
        value: shipperRateUpdatesAllowed,
        options: SHIPPER_RATE_UPDATES_ALLOWED_OPTIONS,
      },
    ]
    const methods = useShipperEdiUpdateSettingsForm({})

    const [showError, setShowError] = useState<boolean>(false)
    const [updateShipperApplicationEdiConfig] = useUpdateShipperApplicationEdiConfigMutation()

    const submitHandler = methods.handleSubmit(async result => {
      setShowError(false)

      if (!configId) {
        setShowError(true)
      } else {
        const response = await updateShipperApplicationEdiConfig({
          variables: {
            input: {
              id: configId,
              ...result,
            },
          },
          refetchQueries: ['ShipperApplicationEdiConfig'],
        })

        if (get(response, 'data.updateShipperApplicationEdiConfig.errors.length')) {
          setShowError(true)
        } else {
          setIsEditing(false)
        }
      }
    })

    return (
      <EditingForm
        isEditing={isEditing}
        onCancel={toggleEditing}
        onSubmit={submitHandler}
        formMethods={methods}>
        <Card>
          <Box mb={2} display='flex' alignItems='center'>
            <Typography variant='h3'>
              {isEditing ? 'Edit EDI Updates Settings' : 'Updates'}
            </Typography>
            <Box ml='auto'>
              <IconButton Icon={EditIcon} color='tertiary' onClick={toggleEditing} size='large' />
            </Box>
          </Box>
          {showError && (
            <Box my={1}>
              <InlineMessage
                type={INLINE_MESSAGE_VARIANTS.ALERT}
                message='Error editing the general settings. Try again. If the issue persists contact tech support.'
              />
            </Box>
          )}
          {rows.map((row, index) => (
            <div key={row.label?.toLowerCase()}>
              <Grid container direction='row' justifyContent='space-between' alignItems='center'>
                <Grid item>
                  <Typography variant='body2' color='textPrimary'>
                    {row.label}
                  </Typography>
                </Grid>
                <Grid item>
                  {isEditing ? (
                    <ControlledSingleSelect
                      control={methods.control}
                      name={row.key}
                      defaultValue={row.value}
                      SingleSelectProps={{
                        id: row.key,
                        dataTest: `${row.key}-single-select`,
                        options: row.options,
                        label: row.selectLabel,
                        clearable: false,
                      }}
                    />
                  ) : (
                    <Pill
                      variant={
                        typeof row.value === 'boolean'
                          ? `${row.value ? 'success' : 'error'}`
                          : 'info'
                      }
                      label={capitalize(row.selectLabel?.toString() || DISPLAY.empty)}
                    />
                  )}
                </Grid>
              </Grid>
              {index !== rows.length - 1 && <hr />}
            </div>
          ))}
        </Card>
      </EditingForm>
    )
  }
)
